/*
 * @Description: AI任务同步接口 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-05 11:55:57
 */
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { syncTask as syncTaskAction } from '../../../actions/aiTask'

// 定义照片相似任务名称常量
export const PHOTO_SIMILAR_TASK_NAME = 'photo_similar' as const

/**
 * 定义照片相似任务路由类型
 * - similar_list: 获取相似照片分组列表
 * - similar_all: 获取某个分组下的所有相似照片
 * - func_status: 检查相似照片功能是否可用
 */
export type PhotoSimilarTaskRoute =
  | 'similar_list'
  | 'similar_all'
  | 'func_status'

/**
 * 通用同步任务请求参数接口
 */
export interface SyncTaskParams<
  TInput extends Record<string, any> | string = Record<string, any> | string
> {
  /** 用户ID，可选，通常由后端自动获取 */
  userID?: string
  /** 任务ID，可选 */
  taskID?: string
  /** 输入参数，可以是对象或JSON字符串 */
  inputParam: TInput
  /** 任务名称，如 'photo_similar' */
  taskName: string
  /** 任务路由 */
  taskRoute: PhotoSimilarTaskRoute | string
}

/**
 * 同步任务响应数据结构
 */
export interface SyncTaskResponseData {
  /** 任务状态码 */
  status: number
  /** 状态描述信息 */
  msg: string
  /** 任务ID */
  taskID: string
  /** 任务名称 */
  taskName: string
  /** 任务路由 */
  taskRoute: string
  /** 输出参数，通常是JSON字符串 */
  outputParam: string
}

/**
 * 同步任务完整响应接口
 */
export interface SyncTaskResponse<TOutput = any> {
  /** 接口状态码 */
  code?: number
  /** 接口消息 */
  msg?: string
  /** 提示消息 */
  toast_msg?: string
  /** 响应数据主体 */
  data?: SyncTaskResponseData
  /** 解析后的输出参数 */
  output?: TOutput | null
}

/**
 * 同步任务配置选项
 */
export interface SyncTaskOptions {
  /**
   * 超时时间（毫秒），默认 60s
   */
  timeoutMs?: number
}

/**
 * 获取相似照片列表请求参数
 */
export interface PhotoSimilarListInput {
  /** 相册ID */
  group_id: string
  /** 每页的相似组数量，默认 20 */
  similar_size?: number
  /** 每个相似组的图像数量，默认 12 */
  chunk_size?: number
  /** 开始时间，毫秒时间戳 */
  start_time?: number
  /** 结束时间，毫秒时间戳 */
  end_time?: number
  /** 分页游标，用于加载下一页。格式示例：{"last_similar_id":"sim_xxx","last_max_time":1770088691702}，第一页传空字符串 */
  page_token?: string
}

/**
 * 获取某个分组所有相似照片请求参数
 */
export interface PhotoSimilarAllInput {
  /** 相册ID */
  group_id: string
  /** 相似组ID */
  similar_id: string
  /** 分页展示的图像数量，默认 50 */
  chunk_size?: number
  /** 分页 Token，第一页传空 */
  page_token?: string
  /** 开始时间，毫秒时间戳 */
  start_time?: number
  /** 结束时间，毫秒时间戳 */
  end_time?: number
}

/**
 * 检查功能状态请求参数
 */
export interface PhotoSimilarFuncStatusInput {
  /** 相册ID */
  group_id: string
}

/**
 * 照片基本信息接口
 */
export interface PhotoInfo {
  /** 照片唯一序列号 */
  photo_seq: string
  /** 照片URL */
  url: string
  /** 是否有水印 */
  has_watermark: boolean
}

/**
 * 相似照片分组项接口
 */
export interface PhotoSimilarListItem {
  /** 相似组ID */
  similar_id: string
  /** 该组下的照片列表 */
  photo_list: PhotoInfo[]
}

/**
 * 获取相似照片列表输出参数
 */
export interface PhotoSimilarListOutput {
  /** 是否有更多数据 */
  has_more: boolean
  /** 下一页的分页Token */
  page_token: string
  /** 结果列表 */
  res_list: PhotoSimilarListItem[]
}

/**
 * 获取分组所有照片输出参数
 */
export interface PhotoSimilarAllOutput {
  /** 是否有更多数据 */
  has_more: boolean
  /** 下一页的分页Token */
  page_token: string
  /** 结果列表 */
  res_list: PhotoInfo[]
}

/**
 * 功能状态输出参数
 */
export interface PhotoSimilarFuncStatusOutput {
  /** 功能是否可用 */
  has_func: boolean
}

const DEFAULT_TIMEOUT_MS = 60 * 1000 // 默认超时时间 60秒

// 统一将 inputParam 规整为 JSON 字符串，保证后端接收格式一致
const normalizeInputParam = (inputParam: Record<string, any> | string) =>
  typeof inputParam === 'string' ? inputParam : JSON.stringify(inputParam)

// outputParam 是 JSON 字符串时解析为对象，解析失败则返回 null
const parseOutputParam = <T,>(outputParam?: string): T | null => {
  if (!outputParam) return null
  try {
    return JSON.parse(outputParam) as T
  } catch (error) {
    console.warn('解析 outputParam 失败:', error)
    return null
  }
}

// 为请求增加超时保护（默认 60s）
const withTimeout = async <T,>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | null = null
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error('请求超时'))
    }, timeoutMs)
  })
  try {
    return (await Promise.race([promise, timeoutPromise])) as T
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * AI 任务同步事件 Hook
 * 提供通用的同步任务调用方法及特定业务场景的封装方法
 */
const useAITaskEvent = () => {
  // 使用 any dispatch，避免 thunk 类型导致的 TS 报错
  const dispatch = useDispatch<any>()

  /**
   * 通用同步任务调用方法
   * @param params 任务参数
   * @param options 配置选项
   */
  const syncTask = useCallback(
    async <
      TOutput = any,
      TInput extends Record<string, any> | string = Record<string, any> | string
    >(
      params: SyncTaskParams<TInput>,
      options?: SyncTaskOptions
    ): Promise<SyncTaskResponse<TOutput>> => {
      const payload = {
        ...params,
        inputParam: normalizeInputParam(params.inputParam)
      }
      const timeoutMs =
        typeof options?.timeoutMs === 'number'
          ? options.timeoutMs
          : DEFAULT_TIMEOUT_MS
      try {
        const result = (await withTimeout(
          dispatch(syncTaskAction(payload)) as Promise<SyncTaskResponse>,
          timeoutMs
        )) as SyncTaskResponse
        return {
          ...result,
          output: parseOutputParam<TOutput>(result.data?.outputParam)
        }
      } catch (error) {
        console.error('Sync task error:', error)
        throw error
      }
    },
    [dispatch]
  )

  /**
   * 获取相似照片分组列表
   */
  const syncPhotoSimilarList = useCallback(
    (input: PhotoSimilarListInput, options?: SyncTaskOptions) =>
      syncTask<PhotoSimilarListOutput, PhotoSimilarListInput>(
        {
          taskName: PHOTO_SIMILAR_TASK_NAME,
          taskRoute: 'similar_list',
          inputParam: input
        },
        options
      ),
    [syncTask]
  )

  /**
   * 获取指定分组下的所有相似照片
   */
  const syncPhotoSimilarAll = useCallback(
    (input: PhotoSimilarAllInput, options?: SyncTaskOptions) =>
      syncTask<PhotoSimilarAllOutput, PhotoSimilarAllInput>(
        {
          taskName: PHOTO_SIMILAR_TASK_NAME,
          taskRoute: 'similar_all',
          inputParam: input
        },
        options
      ),
    [syncTask]
  )

  /**
   * 检查相似照片功能是否可用
   */
  const syncPhotoSimilarFuncStatus = useCallback(
    (input: PhotoSimilarFuncStatusInput, options?: SyncTaskOptions) =>
      syncTask<PhotoSimilarFuncStatusOutput, PhotoSimilarFuncStatusInput>(
        {
          taskName: PHOTO_SIMILAR_TASK_NAME,
          taskRoute: 'func_status',
          inputParam: input
        },
        options
      ),
    [syncTask]
  )

  return {
    syncTask,
    syncPhotoSimilarList,
    syncPhotoSimilarAll,
    syncPhotoSimilarFuncStatus
  }
}

export default useAITaskEvent
